import { icons, SmartButton } from '@tunasong/icons'
import type { SvgIconComponent } from '@tunasong/icons'
import type { Agent, Persisted } from '@tunasong/schemas'
import { defaultAgentName, isDefaultAgentName } from '@tunasong/schemas'
import { resolveDefaultColor, resolveDefaultIconKey } from './role-defaults.js'

/**
 * Display label for an agent: its name, then its definition's stock name ("Agent" without one). Never
 * `roleLine`, which is the model-facing prompt: a legacy unnamed agent would otherwise show its system prompt
 * as its tab label, roster name and chat label (Joni, #2477).
 */
export const getAgentDisplayName = (agent: Pick<Agent, 'name' | 'definitionId'>): string =>
  agent.name?.trim() || defaultAgentName(agent.definitionId ?? '')

/** What an agent is for, in a few words, by role, for agents with no definition. */
const roleLabels: Record<Agent['role'], string> = {
  'song-agent': 'songwriting collaborator',
  'project-agent': 'project assistant',
  'folder-agent': 'library assistant',
  generic: 'assistant',
}

/**
 * The accessible name for an agent where its role isn't on screen (tabs, roster menus): "Nils, Chord
 * Advisor". A name the band chose must never hide what the agent is for, and the band may share it with a
 * bandmate (Joni, Personalize spec §4). A stock name already says what the agent is, so it stands alone.
 *
 * The role comes from the definition's stock name or a short role label, never from `roleLine`: an agent
 * added from the palette carries its whole system prompt there.
 */
export const getAgentAccessibleName = (agent: Pick<Agent, 'name' | 'roleLine' | 'definitionId' | 'role'>): string => {
  const name = getAgentDisplayName(agent)
  if (!agent.name?.trim() || isDefaultAgentName(agent)) {
    return name
  }
  const stock = defaultAgentName(agent.definitionId ?? '')
  const role = stock !== defaultAgentName('') ? stock : (roleLabels[agent.role] ?? roleLabels.generic)
  return name.toLowerCase() === role.toLowerCase() ? name : `${name}, ${role}`
}

/**
 * Resolve the `SvgIconComponent` to use for an agent.
 *
 * Order:
 *  1. `agent.iconKey` (when set and a valid key in the `@tunasong/icons` registry)
 *  2. the live definition's `iconKey`, then the role-keyed default (see {@link resolveDefaultIconKey})
 *  3. generic fallback (`SmartButton`) — used when both the explicit key and
 *     the role-default key are absent from the registry. The registry is the
 *     single source of truth; the schema layer cannot enforce this because the
 *     registry isn't statically known there.
 *
 * The lookup is a plain map read; no hooks, no fetches — safe to call inside
 * render bodies or memoised callbacks.
 */
export const resolveAgentIcon = (agent: Persisted<Agent> | Agent): SvgIconComponent => {
  const key = resolveDefaultIconKey(agent)
  const candidate = icons[key as keyof typeof icons]
  if (candidate) {
    return candidate
  }
  // Unknown key — fall back to the generic icon so the surface still renders.
  return SmartButton
}

export interface AgentVisuals {
  color: string
  Icon: SvgIconComponent
}

/**
 * Bundled `{ color, Icon }` lookup. Surfaces that need both — rail card,
 * sidebar tab, palette command, chat avatar — call this once and avoid
 * resolving the role twice.
 */
export const resolveAgentVisuals = (agent: Persisted<Agent> | Agent): AgentVisuals => ({
  color: resolveDefaultColor(agent),
  Icon: resolveAgentIcon(agent),
})
